import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import type { IncomingInvoiceListItem } from "@msuite/utils/types";
import React from "react";
import { useCitadel } from "@/hooks/use-citadel";
import { ExportPaymentsModal } from "@/modals/incoming-invoices";

/** Props Interface */
interface ExportPaymentsButtonProps {
	incomingInvoices: IncomingInvoiceListItem[];
	onComplete: () => void;
}

/**
 * The export of a payment file over the approved invoices picked in the table. Hidden from a
 * caller without `update IncomingInvoice`, whom the route refuses.
 */
export function ExportPaymentsButton({ incomingInvoices, onComplete }: ExportPaymentsButtonProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { citadel } = useCitadel();
	const exportModal = useDisclosure();

	/** Guard */
	if (!citadel || citadel.cannot("update", "IncomingInvoice")) return null;

	/** Render */
	return (
		<React.Fragment>
			<Button
				size="xs"
				icon={<Icon name="Landmark" />}
				onClick={exportModal.onOpen}
				isDisabled={!incomingInvoices.length}
				data-cy="export-incoming-invoice-payments-button"
			>
				{incomingInvoices.length
					? t("incoming_invoices.payments.export_payments_count", {
							count: `${incomingInvoices.length}`,
						})
					: t("incoming_invoices.payments.export_payments")}
			</Button>
			{exportModal.isOpen && (
				<ExportPaymentsModal
					isOpen={exportModal.isOpen}
					onClose={exportModal.onClose}
					incomingInvoices={incomingInvoices}
					onComplete={onComplete}
				/>
			)}
		</React.Fragment>
	);
}
